/**
 * Per-institute branding — TOKEN_CONTRACT §5, design-system Phase 7.1.
 *
 * Until this existed, Settings → Branding wrote `Institute.primaryColor` to the
 * database and nothing in the app ever read it (audit F7): every surface was
 * hardcoded to the Learnify violet. Phases 1–6 made every one of those surfaces
 * read `hsl(var(--primary))` instead, which is what makes this file a handful of
 * `setProperty` calls rather than a rewrite.
 *
 * Everything here writes INLINE custom properties on <html>, which beat the
 * `:root` / `:root[data-theme="dark"]` declarations in `styles/tokens/_palette.scss`
 * by origin, not by specificity. Removing them restores the palette exactly.
 *
 * ⚠️ Shape matters (TOKEN_CONTRACT §3). `--primary` and friends are BARE HSL
 * TRIPLETS, consumed as `hsl(var(--primary))` or `hsl(var(--primary) / 0.1)`.
 * `--sidebar-active-*` are full colours, consumed as `var(--sidebar-active-bg)`.
 * Writing one shape into the other's slot computes to invalid CSS and the browser
 * silently drops the whole declaration.
 */

/**
 * The palette's own brand hue (`--primary: 221 83% 53%`) as a hex.
 *
 * It round-trips: `hexToHsl(DEFAULT_BRAND_HEX)` is exactly `{h:221, s:83, l:53}`,
 * so feeding it back through `brandTokenValues` reproduces `_palette.scss`'s
 * seven values byte for byte. That is what makes "no brand set" and "brand set to
 * the default" the same screen, and it is worth re-checking if either moves.
 */
export const DEFAULT_BRAND_HEX = '#2463eb';

/** Ink for a light brand colour — the palette's `--foreground` (#0f172a). */
const DARK_INK = '222 47% 11%';
const DARK_INK_LUMINANCE = 0.0106;
const LIGHT_INK = '0 0% 100%';
const LIGHT_INK_LUMINANCE = 1;

/**
 * Dark-mode lightness: lift by 10, then hold it inside 58–80.
 *
 * ── Why a lift and not just a floor ─────────────────────────────────────────
 * This used to be `Math.max(l, 45)` — a floor, which only touched brands dark
 * enough to disappear against `#0b0d12`. That misses the commoner failure:
 * `--primary` is read as INK at least as often as it is used as a fill, and a
 * perfectly visible mid-tone brand is still unreadable AS TEXT on the dark card.
 * The default blue measures 3.45:1 there at its light lightness and 5.04:1 after
 * the lift; the old floor left it at 3.45:1 because 53 > 45.
 *
 * 58 is where a saturated hue clears 4.5:1 on `--card-bg` (#14171f). 80 is the
 * other end — above it a brand is close enough to white that it stops reading as
 * a colour at all, and `pickForeground` would be choosing ink for a near-white
 * button.
 *
 * ⚠️ It reproduces `_palette.scss`'s dark `--primary` exactly for the default
 * hex (53 → 63). If either number moves, the palette's dark block moves with it.
 */
const DARK_LIGHTNESS_LIFT = 10;
const DARK_MIN_LIGHTNESS = 58;
const DARK_MAX_LIGHTNESS = 80;

/**
 * The SOLID fill's floor in dark mode — and it is the only adjustment it gets.
 *
 * `--primary-solid` keeps the institute's own lightness, so the primary button
 * looks the same in both themes. That is right for every brand except a very
 * dark one: charcoal (`#333`, l = 20) measures **1.54:1** against the dark page
 * (`#0b0d12`), which is a button you cannot see the edges of. 3:1 is the bar for
 * a UI shape and a neutral clears it at l ≈ 40 (3.38:1), where white ink is
 * still 5.74:1 — so lifting to the floor costs the ink nothing.
 *
 * A FLOOR, not the lift `--primary` gets: a brand already above it keeps its
 * exact value, and there is no ceiling, because a near-white brand is perfectly
 * visible on a dark page and `pickForeground` gives it dark ink.
 */
const DARK_SOLID_MIN_LIGHTNESS = 40;

/** The five brand tokens plus the two chrome surfaces derived from them. */
const BRAND_PROPERTIES = [
  '--primary',
  '--primary-foreground',
  '--primary-solid',
  '--primary-solid-fg',
  '--primary-soft',
  '--primary-soft-fg',
  '--ring',
  '--sidebar-active-bg',
  '--sidebar-active-fg',
];

/**
 * The four status families an institute may override, and nothing else.
 *
 * ⚠️ The CHART ramp is deliberately absent. `--chart-1` is documented as not
 * following the brand — a series that changed hue per institute would make two
 * institutes' reports incomparable. `--chart-2..5` are POINTERS at the four
 * families below, so they follow these for free and must not be written here:
 * writing `--chart-2` inline would break the pointer and leave the two halves of
 * one hue able to drift again.
 *
 * Surfaces and the neutral ramp are absent too — those are the light and dark
 * themes, which the viewer picks, not the institute.
 */
const STATUS_FAMILIES = ['success', 'warning', 'danger', 'info'];

/** Every property this module may write. Clearing means removing all of them. */
const MANAGED_PROPERTIES = [
  ...BRAND_PROPERTIES,
  ...STATUS_FAMILIES.flatMap((f) => [`--${f}`, `--${f}-soft`, `--${f}-fg`]),
];

/**
 * `#abc` / `#aabbcc` → `{ h, s, l, r, g, b }`, or null for anything else.
 * Returning null rather than throwing is deliberate: a malformed value in the
 * database must leave the default palette standing, not break the whole app.
 */
export function hexToHsl(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;

  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let hue = 0;
  let sat = 0;

  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }

  return {
    h: Math.round(hue * 360),
    s: Math.round(sat * 100),
    l: Math.round(l * 100),
    r,
    g,
    b,
  };
}

/**
 * WCAG relative luminance. The contract calls the `l > 60` shortcut "the minimum";
 * this is the proper check, and it matters — HSL lightness treats a saturated
 * yellow (`#ffd400`, l = 50 %) as mid-tone when it is in fact brighter than a
 * mid-grey and needs dark ink.
 */
export function relativeLuminance({ r, g, b }) {
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Current theme, read from the DOM so this does not depend on provider order. */
const currentTheme = () =>
  (typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme')) === 'dark'
    ? 'dark'
    : 'light';

/**
 * The seven values, given a brand hue and a theme. Exported for the Settings
 * preview, which needs them without touching the live document.
 */
export function brandTokenValues(primaryHex, theme = 'light') {
  const hsl = hexToHsl(primaryHex);
  if (!hsl) return null;

  const { h, s } = hsl;
  const dark = theme === 'dark';
  const l = dark
    ? Math.min(Math.max(hsl.l + DARK_LIGHTNESS_LIFT, DARK_MIN_LIGHTNESS), DARK_MAX_LIGHTNESS)
    : hsl.l;

  // Luminance is measured on the colour actually painted. In dark mode that is
  // the lifted one, so recompute rather than reusing the input's channels.
  const painted = dark && l !== hsl.l ? hslToRgb(h, s, l) : hsl;
  const foreground = pickForeground(relativeLuminance(painted));

  // The SOLID fill keeps the brand's own lightness in both themes — floored in
  // dark only so a charcoal brand is still a visible button. See the constant.
  const solidL = dark ? Math.max(hsl.l, DARK_SOLID_MIN_LIGHTNESS) : hsl.l;
  const solidPainted = solidL !== hsl.l ? hslToRgb(h, s, solidL) : hsl;

  // Derived saturations SCALE with the brand's own, they are not constants.
  // TOKEN_CONTRACT §5 writes `${h} 66% 98%` flat, which is only ever right for
  // one saturation and wrong for anything desaturated: an institute branded
  // charcoal (s = 0) would get a pale PINK soft tint, because a fixed 66 %
  // saturation invents colour the brand does not have. Multiplying degrades to
  // grey at s = 0, and reproduces `_palette.scss` exactly at the default's s = 83.
  const sat = (factor) => Math.round(s * factor);

  return {
    '--primary': `${h} ${s}% ${l}%`,
    '--primary-foreground': foreground,
    // The SOLID fill and its ink — the institute's own lightness, UNLIFTED, in
    // both themes, so a primary button looks the same in dark as in light.
    //
    // ⚠️ It cannot share `--primary`'s dark value, and that is arithmetic rather
    // than taste. Measured at the default hue (221 / 83 %): the brand needs
    // L ≥ ~60 % to clear 4.5:1 as INK on `--card-bg`, and L ≤ ~56 % for white to
    // clear 4.5:1 as ink ON it. The two windows do not overlap, so one token
    // cannot be both — which is why every filled brand button in dark mode came
    // out with the near-black ink `pickForeground` correctly chose for the
    // LIFTED colour. Splitting the fill out is the only fix that keeps both.
    //
    // The ink is computed on the colour actually painted, not assumed white: a
    // pale brand still needs dark ink, and that is the point of pickForeground.
    '--primary-solid': `${h} ${s}% ${solidL}%`,
    '--primary-solid-fg': pickForeground(relativeLuminance(solidPainted)),
    // Soft pair: a tint to sit behind the brand, and an ink readable on it.
    // The two themes invert — light puts dark ink on a near-white tint, dark
    // puts near-white ink on a deep tint — which is why they are not one formula.
    '--primary-soft': dark ? `${h} ${sat(0.40)}% 22%` : `${h} ${sat(0.66)}% 98%`,
    '--primary-soft-fg': dark ? `${h} ${sat(0.92)}% 88%` : `${h} ${sat(0.70)}% 40%`,
    '--ring': `${h} ${s}% ${l}%`,
    // Chrome surfaces. Full colours, not triplets — see the shape warning above.
    //
    // ⚠️ 94% light / 22% dark, deepened from 97% / 18% on 2026-09-01. The
    // sidebar is a raised white surface now, and at 97% the selected row was
    // fainter than the neutral hover sitting next to it — the transient state
    // out-shouted the persistent one. Keep in step with `--sidebar-active-bg`
    // in styles/tokens/_palette.scss: at the default hue these two must agree
    // byte for byte, or "no brand set" and "brand set to the default" become
    // two different screens.
    '--sidebar-active-bg': dark ? `hsl(${h} ${sat(0.30)}% 22%)` : `hsl(${h} ${s}% 94%)`,
    '--sidebar-active-fg': dark ? `hsl(${h} ${sat(0.92)}% 78%)` : `hsl(${h} ${s}% ${l}%)`,
  };
}

/**
 * White ink or near-black, whichever actually reads on the brand colour.
 *
 * A lightness threshold is not good enough and neither is a luminance one: the
 * crossover between the two inks sits at L ≈ 0.20, nowhere near the "is it a
 * light colour" intuition. `#bbaadd` measures L = 0.446 — comfortably "light" by
 * eye and by HSL — yet white on it is 2.1:1 and the dark ink is 8.2:1. So
 * compute both contrasts and take the winner, which needs no threshold at all.
 */
function pickForeground(luminance) {
  const contrast = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  return contrast(luminance, LIGHT_INK_LUMINANCE) >= contrast(luminance, DARK_INK_LUMINANCE)
    ? LIGHT_INK
    : DARK_INK;
}

/** HSL (h 0-360, s/l 0-100) → the 0-1 rgb channels `relativeLuminance` wants. */
function hslToRgb(h, s, l) {
  const S = s / 100;
  const L = l / 100;
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = L - c / 2;
  const seg = Math.floor(h / 60) % 6;
  const [r, g, b] = [
    [c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x],
  ][seg < 0 ? seg + 6 : seg];
  return { r: r + m, g: g + m, b: b + m };
}

/**
 * Contrast of the chosen ink against the brand colour, for the Settings preview.
 * The preview is meant to be honest, and "honest" includes telling an institute
 * that the pastel they just picked gives a 2.8:1 button they will not be able to
 * read — which is exactly the failure the computed foreground is there to avoid,
 * and the one case where it cannot fully save them.
 */
export function brandForegroundContrast(primaryHex, theme = 'light') {
  const values = brandTokenValues(primaryHex, theme);
  if (!values) return null;
  /* ⚠️ The SOLID pair, because that is the pair the preview swatch — and every
     filled brand button in the app — actually paints. `--primary` /
     `--primary-foreground` are the same values in light and DIFFERENT ones in
     dark, so measuring those would report a number for a button nobody sees. */
  const [, sPct, lPct] = values['--primary-solid'].split(' ');
  const h = Number(values['--primary-solid'].split(' ')[0]);
  const painted = hslToRgb(h, parseFloat(sPct), parseFloat(lPct));
  const bg = relativeLuminance(painted);
  const isLightInk = values['--primary-solid-fg'] === LIGHT_INK;
  const ink = isLightInk ? LIGHT_INK_LUMINANCE : DARK_INK_LUMINANCE;
  const ratio = (Math.max(bg, ink) + 0.05) / (Math.min(bg, ink) + 0.05);
  return { ratio, ink: isLightInk ? 'white' : 'dark', passesAA: ratio >= 4.5 };
}

/**
 * The three values of ONE status family, given a hue the institute picked.
 *
 * Same shape as `_palette.scss`'s own four families (§4.4): a base, a tint to
 * sit behind it, and an ink that is READABLE ON THAT TINT. The constants below
 * are read off the palette rather than invented — feeding it `#10b981` (its own
 * success green) reproduces `158 64% 95%` / `158 64% 20%` against the palette's
 * `95%` / `24%`, which is inside the band the four families already span.
 *
 * ⚠️ The base LIFTS in dark for the same reason `--primary` does: a status
 * colour is read as ink (`tw-text-success` on a row, an icon, a count) at least
 * as often as it is a fill, and the light value is unreadable on `--card-bg`.
 */
function statusTokenValues(family, primaryHex, theme = 'light') {
  const hsl = hexToHsl(primaryHex);
  if (!hsl) return null;
  const { h, s, l } = hsl;
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

  if (theme === 'dark') {
    return {
      [`--${family}`]: `${h} ${s}% ${clamp(l + 8, 48, 72)}%`,
      // The tint desaturates as it deepens, or a dark "soft" background reads as
      // a saturated block rather than a wash.
      [`--${family}-soft`]: `${h} ${Math.round(s * 0.45)}% 18%`,
      [`--${family}-fg`]: `${h} ${s}% 72%`,
    };
  }
  return {
    [`--${family}`]: `${h} ${s}% ${l}%`,
    [`--${family}-soft`]: `${h} ${s}% 95%`,
    [`--${family}-fg`]: `${h} ${s}% ${clamp(l - 22, 20, 38)}%`,
  };
}

/**
 * An `appTheme` document → every custom property to write, for one theme.
 *
 * `mode: 'default'` and a missing/blank colour both mean "the palette keeps
 * this one", so an institute that only wants its brand hue changed leaves the
 * four status colours empty and they are simply absent from the result.
 *
 * Exported for the Settings preview, which needs the values without touching
 * the live document.
 */
export function themeTokenValues(appTheme, theme = 'light') {
  if (!appTheme || appTheme.mode !== 'custom') return {};
  const out = {};
  const brand = appTheme.primary ? brandTokenValues(appTheme.primary, theme) : null;
  if (brand) Object.assign(out, brand);
  for (const family of STATUS_FAMILIES) {
    const values = appTheme[family] ? statusTokenValues(family, appTheme[family], theme) : null;
    if (values) Object.assign(out, values);
  }
  return out;
}

/** Contrast of two hex colours, for the Settings preview's honesty check. */
export function contrastOf(hexA, hexB) {
  const a = hexToHsl(hexA);
  const b = hexToHsl(hexB);
  if (!a || !b) return null;
  const [x, y] = [relativeLuminance(a), relativeLuminance(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

let lastAppliedTheme = null;
let themeObserver = null;

/**
 * Apply an institute's theme to the live document.
 *
 * Idempotent, and safe to call with null / `{ mode: 'default' }` — that removes
 * every managed property and hands the app back to `_palette.scss`. Removing is
 * not the same as writing the palette's values: an inline property beats a
 * stylesheet by ORIGIN, so a written-back "default" would freeze the app at
 * today's palette and stop it ever being changed in CSS again.
 *
 * ⚠️ It takes the appTheme OBJECT, not a hex. It used to take
 * `InstituteSettings.primaryColor` — which is the marketing site's colour, read
 * by vc_cms — so retinting the dashboard repainted the public website, and an
 * institute that had merely uploaded a logo got a colour override it never
 * chose. `appTheme` is the LMS's own block and is never sent to vc_cms.
 */
export function applyBrandTokens(appTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  const values = themeTokenValues(appTheme, currentTheme());

  // Always clear first: a colour the institute has just blanked has to stop
  // being painted, and only removal (not overwriting) gives the palette back.
  MANAGED_PROPERTIES.forEach((p) => root.style.removeProperty(p));

  const entries = Object.entries(values);
  if (!entries.length) {
    lastAppliedTheme = null;
    return;
  }

  entries.forEach(([prop, value]) => root.style.setProperty(prop, value));
  lastAppliedTheme = appTheme;

  // Half the derived values differ by theme, and the theme can change at any
  // time from the TopNav toggle. One observer, registered on first use, keeps
  // them correct without BrandingProvider having to sit inside ThemeProvider —
  // which it does not (see src/index.js: Branding is the outer of the two).
  if (!themeObserver && typeof MutationObserver !== 'undefined') {
    themeObserver = new MutationObserver(() => {
      if (lastAppliedTheme) applyBrandTokens(lastAppliedTheme);
    });
    themeObserver.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
  }
}

export default applyBrandTokens;
